import find from 'lodash/find';
import last from 'lodash/last';
import memoize from 'lodash/memoize';

import getNormalTrackLineRequest from './getTrackLineRequests/getNormalRequest';

interface SnapTrackPointProps {
    point: TrackPoint;
    track: Track;
    project: MapProject;
    interaction: InteractionState | undefined;
}

// We use `memoizedFetchLineEnd` and `memoizedFetchLineStart` to ensure that the `Promise`s returned
// by `snapTrackPoint` are memoized with respect to the output of `getNormalTrackLineRequest`.
// We do not need to memoize the plain `GeoJSON.Point` objects because `getSyncPromise` will not
// try to cache them.
const memoizedFetchLineEnd = memoize(
    async (geometry: Promise<GeoJSON.LineString>): Promise<GeoJSON.Point> => {
        return {
            type: 'Point',
            coordinates: last((await geometry).coordinates)!,
        };
    }
);

const memoizedFetchLineStart = memoize(
    async (geometry: Promise<GeoJSON.LineString>): Promise<GeoJSON.Point> => {
        return {type: 'Point', coordinates: (await geometry).coordinates[0]};
    }
);

export default function snapTrackPoint({
    point,
    track,
    project,
    interaction,
}: SnapTrackPointProps): GeoJSON.Point | Promise<GeoJSON.Point> {
    if (!track.properties.localId) return point.geometry;

    if (point.properties.trackLocalId !== track.properties.localId) {
        throw new Error();
    }
    if (!track.isGeometryLoaded) return point.geometry;
    if (track.properties.track_type !== 'route') return point.geometry;

    const draggingLocalId =
        interaction && interaction.subtype === 'draggingPoint'
            ? interaction.pointLocalId
            : undefined;

    const prevPoint = getPrevPoint(track, point);
    if (prevPoint) {
        const prevLine = getNormalTrackLineRequest({
            track,
            project,
            draggingLocalId,
            fromPoint: prevPoint,
            toPoint: point,
        });
        if (prevLine.type === 'async') {
            return memoizedFetchLineEnd(prevLine.geometry);
        }
    }

    const nextPoint = getNextPoint(track, point);
    if (nextPoint) {
        const nextLine = getNormalTrackLineRequest({
            track,
            project,
            draggingLocalId,
            fromPoint: point,
            toPoint: nextPoint,
        });
        if (nextLine.type === 'async') {
            return memoizedFetchLineStart(nextLine.geometry);
        }
    }

    return point.geometry;
}

function getNextPoint(track: Track, point: TrackPoint) {
    return track.isGeometryLoaded
        ? find(track.points, (p) => p.properties.order === point.properties.order + 1)
        : undefined;
}

function getPrevPoint(track: Track, point: TrackPoint) {
    return track.isGeometryLoaded
        ? find(track.points, (p) => p.properties.order === point.properties.order - 1)
        : undefined;
}
